import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Button from "../../components/common/ui/button/Button";
import { Mail, Lock } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { PublicFileDisplay } from "./PublicFileDisplay";
import { FormSubmissionViewPage } from "../RequestTemplates/FormSubmissionViewPage.tsx";
import type { ShareLinkInfoDto } from "@secure-caseworker/shared";

export const SharedResourcePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [shareLinkInfo, setShareLinkInfo] = useState<ShareLinkInfoDto | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    checkShareLink();
  }, [token]);

  const checkShareLink = async () => {
    try {
      const data = await apiClient.getShareLinkInfo(token!);
      setShareLinkInfo(data);
      setLoading(false);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load share link";
      if (errorMessage.includes("404")) {
        setError("This share link does not exist or has expired");
      } else {
        setError(errorMessage);
      }
      setLoading(false);
    }
  };

  const handleSendVerification = async () => {
    setSending(true);
    setError(null);

    try {
      // No email param - backend uses stored recipient
      await apiClient.requestShareLinkVerification(token!);
      setEmailSent(true);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to send verification email";
      setError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8">
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Access Denied
            </h1>
            <p className="text-gray-600 dark:text-gray-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Email verification required but not yet verified
  if (
    shareLinkInfo?.mode === "email_verified" &&
    !shareLinkInfo?.isVerified &&
    !emailSent
  ) {
    return (
      <div className="flex items-center justify-center h-full px-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-brand-100 dark:bg-brand-900/20 rounded-full flex items-center justify-center mb-4">
              <Mail className="h-8 w-8 text-brand-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Email Verification Required
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              This content requires email verification. Click the button below
              to receive a verification link.
            </p>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200 mb-4">
              {error}
            </div>
          )}

          <Button
            type="button"
            variant="primary"
            size="md"
            className="w-full"
            onClick={handleSendVerification}
            disabled={sending}
          >
            {sending ? "Sending..." : "Send Verification Email"}
          </Button>

          <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>
              A verification link will be sent to the email address associated
              with this share link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Email sent, waiting for verification
  if (emailSent) {
    return (
      <div className="flex items-center justify-center h-full px-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8 max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-green-600 mb-4">
            Check Your Email
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            We've sent a verification link to the email address associated with
            this share link.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Click the link in the email to access the shared content. The link
            will expire in 15 minutes.
          </p>
        </div>
      </div>
    );
  }

  // Verified or public mode - render the appropriate component
  if (shareLinkInfo?.resourceType === "file") {
    return <PublicFileDisplay />;
  }

  if (shareLinkInfo?.resourceType === "form_submission") {
    return <FormSubmissionViewPage shareToken={token} />;
  }

  return null;
};
